"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";

const ArrowRightIcon = ({ className }) => (
  <svg
    width="15"
    height="10"
    viewBox="0 0 15 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M13.25 4.9165L0.75 4.9165"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.91663 0.75L13.3762 4.20956C13.7095 4.54289 13.8762 4.70956 13.8762 4.91667C13.8762 5.12377 13.7095 5.29044 13.3762 5.62377L9.91663 9.08333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Both outer + inset shadows in every state so CSS can smoothly interpolate between them
const variantStyles = {
  // Primary dark: hover fades outer shadow out, fades cream inner glow in
  primary: cn(
    "bg-[#1E1E1E] text-[#BFBEB8] border border-[rgba(96,96,96,0.4)]",
    "shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent]",
    "hover:border-[#606060]",
    "hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_#bfbeb8]"
  ),
  // Secondary cream: inverts to dark bg + inner glow on hover
  secondary: cn(
    "bg-[#DBDACD] text-[#3E3E3E] border border-[#F8F7F4]",
    "shadow-[inset_0px_0px_0px_0px_transparent]",
    "hover:bg-[#1E1E1E] hover:text-[#DBDACD] hover:border-[#606060]",
    "hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8]"
  ),
  // Secondary outline: dark border, inverts to dark bg + inner glow on hover
  "secondary-outline": cn(
    "bg-[#DBDACD] text-[#3E3E3E] border border-[#3E3E3E]",
    "shadow-[inset_0px_0px_0px_0px_transparent]",
    "hover:bg-[#1E1E1E] hover:text-[#DBDACD] hover:border-[#606060]",
    "hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8]"
  ),
  // AI CTA: gold border, hover fades outer shadow out + fades gold inner glow in
  "ai-cta": cn(
    "bg-[#1E1E1E] text-[#DBDACD] border border-[#FFC520]",
    "shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent]",
    "hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_rgba(255,197,32,0.4)]"
  ),
  // PO CTA: yellow border, hover fades outer shadow out + fades yellow inner glow in
  "po-cta": cn(
    "bg-[#1E1E1E] text-[#DBDACD] border border-[#FCFB00]",
    "shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent]",
    "hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_rgba(252,251,0,0.4)]"
  ),
  // LR CTA: emerald border, hover fades outer shadow out + fades emerald inner glow in
  "lr-cta": cn(
    "bg-[#1E1E1E] text-[#DBDACD] border border-[#3DEDD0]",
    "shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent]",
    "hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_rgba(61,237,208,0.4)]"
  ),
};

const sizeStyles = {
  default: "h-[54px] px-4 py-4 text-[16px] leading-5 tracking-[-0.009em]",
  md: "h-[44px] px-4 py-2.5 text-[14px] leading-5 tracking-[-0.009em]",
  icon: "w-12 h-12 p-0",
};

export const Button = ({
  children,
  onClick,
  href,
  variant = "primary",
  size = "default",
  icon = true,
  IconComponent,
  disabled = false,
  loading = false,
  className,
  type = "button",
  ...props
}) => {
  const showArrow = icon && size !== "icon" && !IconComponent;
  const showCustomIcon = icon && IconComponent;

  const classes = cn(
    "inline-flex items-center justify-between gap-4 whitespace-nowrap rounded-[10px] font-medium cursor-pointer transition-[box-shadow,border-color,background-color,color] duration-500 ease-in-out",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    variantStyles[variant],
    sizeStyles[size],
    size === "icon" && "items-center justify-center",
    className
  );

  const content = loading ? (
    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
  ) : (
    <>
      {size !== "icon" && <span>{children}</span>}
      {size === "icon" && children}
      {showArrow && <ArrowRightIcon className="flex-shrink-0" />}
      {showCustomIcon && <IconComponent className="w-4 h-4 flex-shrink-0" />}
    </>
  );

  if (href) {
    const isExternal =
      typeof href === "string" &&
      (href.startsWith("http") || href.startsWith("mailto:"));

    if (isExternal) {
      return (
        <a
          href={href}
          className={classes}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {content}
        </a>
      );
    }

    return (
      <Link href={href} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      {...props}
    >
      {content}
    </button>
  );
};

export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => (
  <Button variant="secondary" {...props} />
);
